import { Badge } from "@bokio/designsystem/components/Badge/Badge";
import { Tooltip } from "@bokio/designsystem/components/Tooltip";
import Markdown from "@bokio/elements/Markdown/Markdown";
import { usePricePlanFeatureUpgrade } from "@bokio/mobile-web-shared/areas/pricePlan/usePricePlanFeatureUpgrade";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import { usePricePlanContext } from "@bokio/mobile-web-shared/core/contexts/PricePlanContext/PricePlanContext";
import { usePricePlanFeatureContext } from "@bokio/mobile-web-shared/core/contexts/PricePlanFeatureContext/PricePlanFeatureContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useRouter } from "@bokio/shared/containers/router/useRouter";
import { getRoute } from "@bokio/shared/route";
import { useCompanyUser } from "@bokio/shared/state/requests";

import type {
	PricePlanFeatureUpgradeHookBadge,
	PricePlanFeatureUpgradeHookIcon,
} from "@bokio/mobile-web-shared/areas/pricePlan/usePricePlanFeatureUpgrade";
import type { FC, ReactNode } from "react";

import BokioPlan = m.Entities.BokioPlan;
import PricePlanFeature = m.Entities.PricePlanFeature;

export { BokioPlan, PricePlanFeature };

interface BokioPlanLockSharedProps {
	onClick?: () => void;
}

interface BokioPlanLockSharedBadgeProps {
	badgeTestId?: string;
	iconOnly?: boolean;
	allowInteraction?: boolean;
	badgeTooltipTestId?: string;
	badgeTooltopPopoverTestId?: string;
}

interface BokioPlanLockBadgeProps extends BokioPlanLockSharedProps, BokioPlanLockSharedBadgeProps {
	feature: PricePlanFeature;
	/**
	 * Default to true. When false, it will become unclickable and won't show any tooltips on hover.
	 */
	hasUpgradePermission: boolean;
}

export interface BokioPlanLockBadgeHookProps extends BokioPlanLockSharedProps, BokioPlanLockSharedBadgeProps {
	feature: PricePlanFeature;
	/**
	 * If you want to lock the feature based on some other condition, you can use this. It will override the default lock condition.
	 * This should only be used in edge cases, really try to use the builtin feature mappings if possible or add support for the new
	 * functionality in the feature mapping.
	 * @returns the text of the badge to lock, or false to unlock
	 */

	overrideLockConditionAndText?: () => string | false;
}

export interface BokioPlanLockBadgeHookHandle {
	isFeatureLocked?: boolean;
	daysUntilTrialEnded?: number;
	/**
	 * Nullish when user don't have permission
	 */
	ctaRoute?: string;
	onClick?: () => void;
	renderBadge: () => ReactNode;
	hasUpgradePermission: boolean;
	icon: PricePlanFeatureUpgradeHookIcon;
}

interface BokioPlanLockBadgeInnerProps extends BokioPlanLockSharedBadgeProps {
	icon: PricePlanFeatureUpgradeHookIcon;
	isFeatureLocked: boolean;
	ctaRoute?: string;
	onClick?: () => void;
	badge: PricePlanFeatureUpgradeHookBadge;
}

const BokioPlanLockBadgeInner: FC<BokioPlanLockBadgeInnerProps> = ({
	icon,
	isFeatureLocked,
	iconOnly,
	allowInteraction = true,
	ctaRoute,
	onClick,
	badgeTestId,
	badgeTooltipTestId,
	badgeTooltopPopoverTestId,
	badge,
}) => {
	const router = useRouter();

	if (!isFeatureLocked) {
		return null;
	}

	const text = iconOnly ? "" : <Markdown markdownContent={badge.text} inline deprecatedOptoutFromTypographicElements />;

	const toolTipContent = (
		<>
			{badge.tooltipRows.map((text, index) => (
				<Markdown markdownContent={text} key={index} />
			))}
		</>
	);

	const renderBadge = (
		<Badge
			color="amend"
			icon={icon}
			iconPosition="left"
			name={text}
			noMargin
			onClick={
				allowInteraction && ctaRoute
					? () => {
							router.redirect(ctaRoute);
							onClick?.();
						}
					: undefined
			}
			testId={badgeTestId}
		/>
	);

	return (
		// Wrapper span to deal with legacy style issues in Status that causes wrong width/height
		<span>
			{allowInteraction ? (
				// TODO: Known issue that this doesn't really work well with screen right edge at the moment, shouldn't be too hard to fix but takes some time.
				<span>
					<Tooltip
						showArrow={false}
						contentGenerator={() => toolTipContent}
						triggerTestId={badgeTooltipTestId}
						popoverTestId={badgeTooltopPopoverTestId}
					>
						{renderBadge}
					</Tooltip>
				</span>
			) : (
				renderBadge
			)}
		</span>
	);
};

/**
 * @returns The hook for rendering the correct price plan lock information. `undefined` if the feature shouldn't be blocked.
 */
export const useBokioPlanLockBadge = ({
	feature,
	overrideLockConditionAndText,
	onClick,
	badgeTestId,
	badgeTooltipTestId,
	badgeTooltopPopoverTestId,
	iconOnly,
	allowInteraction,
}: BokioPlanLockBadgeHookProps): BokioPlanLockBadgeHookHandle | undefined => {
	const { companyInfo } = useCompanyInfo();
	const { companyUserPermissions } = useCompanyUser();
	const { daysUntilTrialEnded, plan } = usePricePlanContext();
	const { isPricePlanFeatureEnabled, isLoading, getAddonForPricePlanFeature } = usePricePlanFeatureContext();
	const hasUpgradePermission = !!companyUserPermissions?.CompanySettings;
	const upgradeHook = usePricePlanFeatureUpgrade({ feature, hasUpgradePermission });

	if (!feature || isLoading) {
		return undefined;
	}

	const orLockText = overrideLockConditionAndText?.();
	const isFeatureLocked = orLockText === false ? false : !isPricePlanFeatureEnabled(feature);

	const addon = getAddonForPricePlanFeature(feature);
	const ctaRoute = hasUpgradePermission
		? upgradeHook?.actionType === "start-trial-addon" && addon
			? getRoute("billing", { company: companyInfo.Id }, { tab: "plans", startTrialAddon: addon.Type })
			: getRoute("billing", { company: companyInfo.Id })
		: undefined;

	const shouldRender = plan && upgradeHook && isFeatureLocked;

	const renderBadgeProps: BokioPlanLockBadgeInnerProps | undefined = !!orLockText
		? {
				// We just return a locked badge with text because we don't have any thought through rules around what to render.
				isFeatureLocked: true,
				icon: "bolt",
				badge: { text: orLockText, tooltipRows: [] },
				ctaRoute,
				onClick,
				badgeTestId,
				badgeTooltipTestId,
				badgeTooltopPopoverTestId,
				iconOnly,
				allowInteraction,
			}
		: shouldRender
			? {
					isFeatureLocked,
					ctaRoute,
					onClick,
					badgeTestId,
					badgeTooltipTestId,
					badgeTooltopPopoverTestId,
					icon: upgradeHook.icon,
					badge: upgradeHook.badge,
					iconOnly,
					allowInteraction,
				}
			: undefined;
	return renderBadgeProps
		? {
				...renderBadgeProps,
				renderBadge: () => <BokioPlanLockBadgeInner {...renderBadgeProps} />,
				hasUpgradePermission,
				daysUntilTrialEnded,
			}
		: undefined;
};

export const BokioPlanLockBadge: FC<BokioPlanLockBadgeProps> = props => {
	const hook = useBokioPlanLockBadge(props);

	return hook ? hook.renderBadge() : null;
};
